import isEmpty from "lodash/isEmpty";

import {
  parseWebSignupBootstrap,
  toStripeCouponFromBootstrap,
  toViewSubscriptionFromBootstrap,
  WebSignupBootstrap,
} from "../../../Common/utils/webSignupBootstrap";
import { getWLPromoEntries, isPromoValid } from "../../services/promo";
import { trackSignupAddToCart } from "../../services/marketing";
import { bugNotif } from "../../api/bugsnag";
import { isClubPlan } from "../../../Common/constants/clubMembership";
import { getErrorMsg } from "../../../Common/utils/common";
import { logWebSignupTimingOnce } from "./signupTiming";
import useWebSignupStore from "./store";

export const getJoinKeyFromPathname = (pathname: string): string => {
  const match = pathname.match(/\/join\/([^/?#]+)/i);
  return (match?.[1] ?? "").toLowerCase().trim();
};

export const isJoinBootstrapMismatched = (
  pathname: string,
  plan: { docId?: string; name?: string }
): boolean => {
  const pathIsClub = getJoinKeyFromPathname(pathname) === "club";
  return pathIsClub !== isClubPlan(plan);
};

/**
 * The URL check stops the catalog fetch racing a valid bootstrap before the
 * store hydrates. A rejected (mismatched) bootstrap is not usable, so the
 * path-filtered catalog still loads.
 */
export const isWebSignupCatalogFetchEnabled = (params: {
  bootstrapFromQuery: boolean;
  hasUsableLocationBootstrap: boolean;
}): boolean =>
  !params.bootstrapFromQuery && !params.hasUsableLocationBootstrap;

export const readWebSignupBootstrapFromLocation = (): WebSignupBootstrap | null => {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return parseWebSignupBootstrap(window.location.search);
  } catch {
    return null;
  }
};

export const hasUsableWebSignupBootstrapInLocation = (): boolean => {
  const bootstrap = readWebSignupBootstrapFromLocation();
  if (!bootstrap) {
    return false;
  }
  const pathname =
    typeof window === "undefined" ? "" : window.location.pathname;
  return !isJoinBootstrapMismatched(
    pathname,
    toViewSubscriptionFromBootstrap(bootstrap.subscription),
  );
};

/**
 * Non-blocking WL promo hydrate for Order Summary / promo UI.
 * Does not touch validStripeCoupon (already settled by bootstrap).
 */
export const hydrateBootstrapWlPromoInBackground = async (
  promoCode: string,
): Promise<void> => {
  const code = (promoCode || "").trim().toUpperCase();
  if (isEmpty(code)) {
    return;
  }

  try {
    const result = await isPromoValid({
      code,
      forSignUp: true,
    });
    const {
      setValidPromoCodePromo,
      setPromoEntries,
      selectedSubscription,
    } = useWebSignupStore.getState();

    setValidPromoCodePromo(result);
    if (
      result &&
      selectedSubscription !== undefined &&
      selectedSubscription !== null
    ) {
      setPromoEntries(getWLPromoEntries(result, selectedSubscription));
    }
  } catch (error) {
    bugNotif(
      "hydrateBootstrapWlPromoInBackground",
      getErrorMsg(error).rawErrorMsg,
    );
  }
};

/**
 * Hydrates signup store from complete bootstrap query params.
 * Returns true when applied (APIs should skip blocking mount waits).
 */
export const applyWebSignupBootstrapIfPresent = (): boolean => {
  const bootstrap = readWebSignupBootstrapFromLocation();
  if (!bootstrap) {
    logWebSignupTimingOnce(
      "bootstrap-source",
      "prerequisite: bootstrap source",
      { bootstrapSource: "api" },
    );
    return false;
  }

  const {
    setSelectedSubscription,
    setSubscriptionList,
    setValidStripeCoupon,
    setValidPromoCode,
    setValidPromoCodePromo,
    setBootstrapFromQuery,
  } = useWebSignupStore.getState();

  const selectedSubscription = toViewSubscriptionFromBootstrap(
    bootstrap.subscription,
  );
  const pathname =
    typeof window === "undefined" ? "" : window.location.pathname;
  if (isJoinBootstrapMismatched(pathname, selectedSubscription)) {
    logWebSignupTimingOnce(
      "bootstrap-source",
      "prerequisite: bootstrap source",
      {
        bootstrapSource: "api",
        bootstrapRejected: "join-key-mismatch",
        joinKey: getJoinKeyFromPathname(pathname),
        bootstrapName: selectedSubscription.name,
      },
    );
    bugNotif(
      "applyWebSignupBootstrapIfPresent",
      `Rejected bootstrap ${selectedSubscription.name} on ${pathname}`,
    );
    return false;
  }

  const validStripeCoupon = toStripeCouponFromBootstrap(bootstrap.coupon);

  setSelectedSubscription(selectedSubscription);
  setSubscriptionList([selectedSubscription]);
  setValidStripeCoupon(validStripeCoupon);
  setValidPromoCode(bootstrap.promoCode || "");
  setValidPromoCodePromo(null);
  setBootstrapFromQuery(true);

  trackSignupAddToCart({
    subscriptionId: selectedSubscription.docId,
    itemName: selectedSubscription.name,
    value: selectedSubscription.price,
    type: "Membership",
  });

  logWebSignupTimingOnce(
    "bootstrap-source",
    "prerequisite: bootstrap source",
    { bootstrapSource: "query" },
  );
  logWebSignupTimingOnce(
    "selected-subscription-ready",
    "prerequisite: selectedSubscription ready",
    {
      subscriptionName: selectedSubscription.name,
      price: selectedSubscription.price,
      bootstrapSource: "query",
    },
  );
  logWebSignupTimingOnce(
    "valid-stripe-coupon-settled",
    "prerequisite: validStripeCoupon settled",
    {
      hasCoupon: validStripeCoupon !== null,
      couponId: validStripeCoupon?.id,
      bootstrapSource: "query",
    },
  );

  // Do not await — must not block Elements mount.
  void hydrateBootstrapWlPromoInBackground(bootstrap.promoCode);

  return true;
};
